import React from "react";
import { Upload } from "lucide-react";

import { useKbSearch } from "@/shared/api/knowledge";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { KbTable } from "./KbTable";
import { KbToolbar } from "./KbToolbar";
import { NO_FILTERS, filterCount, peopleIn, searchRows, view, type Row } from "./listing";
import { useFileDrop } from "./hooks/useFileDrop";
import type { AddKind } from "./KbAddMenu";
import type { NodeEdits } from "./KbRow";
import type { KbListing } from "./hooks/useListing";
import type { KbTree } from "./hooks/useTree";
import type { PendingUpload } from "./hooks/useKbUpload";

import type { KbNode, KbUpload, KnowledgeBase } from "@/shared/types";

/* One folder (or the base at its top) as a drive lists it: search, filter and sort over a
   table of what it holds; a search reaches across the whole base. Files and folders dropped
   anywhere on it upload here, and a folder row takes a drop into itself. */
export function KbFolderView({ kbId, base, folderId, tree, listing, pendingIn, me, onOpen, edits, onDrop, onAdd, aside }: {
  kbId: string;
  base: KnowledgeBase;
  folderId: string | null;
  tree: KbTree;
  listing: KbListing;
  /** The uploads in flight into a folder of this base. */
  pendingIn: (parentId: string | null) => PendingUpload[];
  me?: string;
  onOpen: (node: KbNode) => void;
  edits: NodeEdits;
  onDrop: (uploads: KbUpload[], folderId: string | null) => void;
  onAdd: (kind: AddKind) => void;
  /** The toolbar's end: New, on a phone. */
  aside?: React.ReactNode;
}) {
  const { query, term, filters, sort } = listing;
  const searching = query.trim().length > 0;
  const search = useKbSearch(kbId, term);
  const here = tree.byId(folderId)?.name ?? base.name;
  const drop = useFileDrop((uploads) => onDrop(uploads, folderId));

  const rows: Row[] = searching
    ? searchRows(tree, query, search.data ?? [], base.name)
    : tree.childrenOf(folderId).map((node) => ({ node }));
  const shown = view(rows, filters, sort, tree);
  const waiting = searching && term.length > 0 && search.isPending && !shown.length;

  // A dropped folder's row is real once its first batch lands: it carries the spinner from
  // then on, in place of a placeholder twin.
  const listed = new Set(tree.childrenOf(folderId).filter((n) => n.kind === "folder").map((n) => n.name.toLowerCase()));
  const arriving = pendingIn(folderId);
  const pending = arriving.filter((p) => !(p.folder && listed.has(p.name.toLowerCase())));
  const uploadingInto = (folder: KbNode) =>
    pendingIn(folder.id).length > 0 || arriving.some((p) => p.folder && p.name.toLowerCase() === folder.name.toLowerCase());

  return (
    <section aria-label="Folder contents" {...drop.props} className="relative flex-1 min-w-0 min-h-0 flex flex-col">
      <KbToolbar
        base={base.name}
        query={query}
        onQuery={listing.setQuery}
        rows={rows}
        shown={shown.length}
        filters={filters}
        onFilters={listing.setFilters}
        sort={sort}
        onSort={listing.setSort}
        people={peopleIn(tree.nodes)}
        me={me}
        aside={aside}
      />
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <div className="px-4 md:px-5 pb-8 fade-in">
          {search.error && searching && (
            <p className="m-0 mb-2 text-[12.5px] text-[var(--text-tertiary)]">Searching inside files failed: {search.error.message}. Names still match.</p>
          )}
          {waiting ? (
            <Skeleton className="h-[120px] rounded-[18px] bg-[var(--surface-2)]" />
          ) : (
            <KbTable
              rows={shown}
              tree={tree}
              me={me}
              sort={sort}
              onSort={listing.setSort}
              pending={searching ? [] : pending}
              uploadingInto={uploadingInto}
              narrowed={{ searching, filtering: filterCount(filters) > 0 }}
              onClear={() => (searching ? listing.setQuery("") : listing.setFilters(NO_FILTERS))}
              onOpen={onOpen}
              edits={edits}
              onDrop={(uploads, folder) => onDrop(uploads, folder.id)}
              onAdd={onAdd}
            />
          )}
        </div>
      </div>
      {drop.over && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-2 rounded-[18px] ring-2 ring-[var(--info)] bg-[var(--info-bg)] flex items-end justify-center pb-6 fade-in">
          <span className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-menu text-[13px] text-[var(--text-primary)]">
            <Upload className="w-3.5 h-3.5 shrink-0 text-[var(--info)]" strokeWidth={1.75} />
            <span className="min-w-0 max-w-[320px] truncate">Drop to upload to <span className="font-medium">{here}</span></span>
          </span>
        </div>
      )}
    </section>
  );
}
